import { flatten } from './merge';
import { isoDate } from './values';
import { isLeaf, text, type Condition, type EvaluationContext, type FieldDef, type FieldType, type FormDefinition, type JsonValue, type LeafCondition, type OptionDef } from './types';

type Lang = 'sk' | 'en';

interface Phrases {
  visible: string;
  enabled: string;
  required: string;
  valid: string;
  document: string;
  option: string;
  and: string;
  or: string;
  not: string;
  yes: string;
  no: string;
  filled: string;
  empty: string;
  eq: string;
  neq: string;
  includes: string;
  excludes: string;
  numGt: string;
  numGte: string;
  numLt: string;
  numLte: string;
  dateGt: string;
  dateGte: string;
  dateLt: string;
  dateLte: string;
  quoteOpen: string;
  quoteClose: string;
  on: string;
  off: string;
}

/** Wording shared with the backend RuleSummarizer (fixtures in spec/form-engine/summaries). */
const PHRASES: Record<Lang, Phrases> = {
  sk: {
    visible: 'Zobrazí sa, ak %s.',
    enabled: 'Dostupné, ak %s.',
    required: 'Povinné, ak %s.',
    valid: 'Platné, ak %s.',
    document: 'Vyplňte, iba ak %s.',
    option: 'Možnosť %s sa zobrazí, ak %s.',
    and: ' a ',
    or: ' alebo ',
    not: 'neplatí, že %s',
    yes: 'áno',
    no: 'nie',
    filled: '%s je vyplnené',
    empty: '%s nie je vyplnené',
    eq: '%s je %s',
    neq: '%s nie je %s',
    includes: '%s obsahuje %s',
    excludes: '%s neobsahuje %s',
    numGt: '%s je väčšie ako %s',
    numGte: '%s je aspoň %s',
    numLt: '%s je menšie ako %s',
    numLte: '%s je najviac %s',
    dateGt: '%s je neskôr ako %s',
    dateGte: '%s je %s alebo neskôr',
    dateLt: '%s je skôr ako %s',
    dateLte: '%s je %s alebo skôr',
    quoteOpen: '„',
    quoteClose: '“',
    on: 'zapnuté',
    off: 'vypnuté',
  },
  en: {
    visible: 'Shown if %s.',
    enabled: 'Available if %s.',
    required: 'Required if %s.',
    valid: 'Valid if %s.',
    document: 'Fill in only if %s.',
    option: 'Option %s is shown if %s.',
    and: ' and ',
    or: ' or ',
    not: 'it is not the case that %s',
    yes: 'yes',
    no: 'no',
    filled: '%s is filled in',
    empty: '%s is empty',
    eq: '%s is %s',
    neq: '%s is not %s',
    includes: '%s includes %s',
    excludes: '%s does not include %s',
    numGt: '%s is greater than %s',
    numGte: '%s is at least %s',
    numLt: '%s is less than %s',
    numLte: '%s is at most %s',
    dateGt: '%s is after %s',
    dateGte: '%s is on or after %s',
    dateLt: '%s is before %s',
    dateLte: '%s is on or before %s',
    quoteOpen: '“',
    quoteClose: '”',
    on: 'on',
    off: 'off',
  },
};

function fmt(template: string, ...args: string[]): string {
  let i = 0;
  return template.replace(/%s/g, () => args[i++] ?? '');
}

const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** sk "1. 10. 2026", en "1 October 2026" (the engine's own formatting, identical to the backend). */
export function formatSummaryDate(iso: string, lang: Lang): string {
  if (!isoDate(iso)) return iso;
  const [y, m, d] = iso.split('-').map(Number);
  return lang === 'en' ? `${d} ${EN_MONTHS[m! - 1]} ${y}` : `${d}. ${m}. ${y}`;
}

export type SummaryRule = 'visibleWhen' | 'enabledWhen' | 'requiredWhen' | 'validWhen' | 'document';

export class RuleSummarizer {
  private readonly index = new Map<string, FieldDef>();

  constructor(
    definition: FormDefinition,
    private readonly context: EvaluationContext = {},
  ) {
    for (const f of flatten(definition.fields)) if (!this.index.has(f.key)) this.index.set(f.key, f);
  }

  /** `rule`: a SummaryRule, or `option:<value>` for the rule of an option. */
  summary(fieldKey: string, rule: SummaryRule | `option:${string}`, lang: Lang): string | null {
    const f = this.index.get(fieldKey);
    if (!f) return null;
    if (rule.startsWith('option:')) {
      const o = f.options?.find((x) => x.value === rule.slice('option:'.length));
      return o ? this.optionSummary(o, lang) : null;
    }
    return this.summaryOf(f, rule as SummaryRule, lang);
  }

  /** "Možnosť „Iné“ sa zobrazí, ak …"; null for an option without a rule. */
  optionSummary(o: OptionDef, lang: Lang): string | null {
    if (!o.visibleWhen) return null;
    const p = PHRASES[lang];
    return fmt(p.option, `${p.quoteOpen}${text(o.label, lang)}${p.quoteClose}`, this.text(o.visibleWhen, lang, false));
  }

  summaryOf(f: FieldDef, rule: SummaryRule, lang: Lang): string | null {
    const p = PHRASES[lang];
    if (rule === 'document') return this.documentSummary(f, lang);
    const c = f[rule];
    if (!c) return null;
    const template = rule === 'visibleWhen' ? p.visible : rule === 'enabledWhen' ? p.enabled : rule === 'requiredWhen' ? p.required : p.valid;
    return fmt(template, this.text(c, lang, false));
  }

  documentSummary(f: FieldDef, lang: Lang): string | null {
    const parts = [f.visibleWhen, f.enabledWhen].filter((c): c is Condition => Boolean(c)).map((c) => this.text(c, lang, true));
    if (parts.length === 0) return null;
    return fmt(PHRASES[lang].document, parts.join(PHRASES[lang].and));
  }

  /** The condition without prefix; `nested` wraps multi-part groups in parentheses. */
  text(c: Condition, lang: Lang, nested: boolean): string {
    const p = PHRASES[lang];
    if ('all' in c) return this.group(c.all, p.and, lang, nested);
    if ('any' in c) return this.group(c.any, p.or, lang, nested);
    if ('not' in c) return fmt(p.not, this.text(c.not, lang, true));
    return isLeaf(c) ? this.leaf(c, lang) : '';
  }

  private group(parts: Condition[], separator: string, lang: Lang, nested: boolean): string {
    if (parts.length === 1) return this.text(parts[0]!, lang, nested);
    const joined = parts.map((x) => this.text(x, lang, true)).join(separator);
    return nested ? `(${joined})` : joined;
  }

  private leaf(leaf: LeafCondition, lang: Lang): string {
    const p = PHRASES[lang];
    const key = leaf.field.startsWith('row.') ? leaf.field.slice(4) : leaf.field;
    const ref = this.index.get(key);
    const label = this.label(leaf.field, lang);
    const type: FieldType = ref?.type ?? 'TEXT';
    const v = leaf.value;
    // compared with another field: its label in place of a value
    const value = () => (leaf.valueField !== undefined ? this.label(leaf.valueField, lang) : this.value(ref, type, v, lang, p.and));
    switch (leaf.op) {
      case 'empty':
        return fmt(p.empty, label);
      case 'notEmpty':
        return fmt(p.filled, label);
      case 'eq':
        return fmt(p.eq, label, value());
      case 'neq':
        return fmt(p.neq, label, value());
      case 'in':
        return fmt(p.eq, label, this.list(ref, type, v, lang, p.or));
      case 'notIn':
        return fmt(p.neq, label, this.list(ref, type, v, lang, p.or));
      case 'includes':
        return fmt(p.includes, label, value());
      case 'excludes':
        return fmt(p.excludes, label, value());
      case 'gt':
        return fmt(type === 'DATE' ? p.dateGt : p.numGt, label, value());
      case 'gte':
        return fmt(type === 'DATE' ? p.dateGte : p.numGte, label, value());
      case 'lt':
        return fmt(type === 'DATE' ? p.dateLt : p.numLt, label, value());
      case 'lte':
        return fmt(type === 'DATE' ? p.dateLte : p.numLte, label, value());
      default:
        return label;
    }
  }

  /** The label of the field a reference ("key" or "row.key") stands for. */
  private label(reference: string, lang: Lang): string {
    const key = reference.startsWith('row.') ? reference.slice(4) : reference;
    const ref = this.index.get(key);
    return ref ? text(ref.label, lang) : key;
  }

  private list(ref: FieldDef | undefined, type: FieldType, v: JsonValue | undefined, lang: Lang, last: string): string {
    if (!Array.isArray(v)) return this.value(ref, type, v, lang, last);
    return join(v.map((x) => this.single(ref, type, x, lang)), last);
  }

  private value(ref: FieldDef | undefined, type: FieldType, v: JsonValue | undefined, lang: Lang, last: string): string {
    if (v === undefined || v === null) return '';
    if (Array.isArray(v)) return join(v.map((x) => this.single(ref, type, x, lang)), last);
    return this.single(ref, type, v, lang);
  }

  private single(ref: FieldDef | undefined, type: FieldType, v: JsonValue, lang: Lang): string {
    const p = PHRASES[lang];
    if (typeof v === 'boolean' && type === 'SWITCH') return v ? p.on : p.off;
    if (typeof v === 'boolean') return v ? p.yes : p.no;
    const s = String(v);
    switch (type) {
      case 'SINGLE_CHOICE':
      case 'MULTI_CHOICE': {
        const o = ref?.options?.find((x) => x.value === s);
        return o ? text(o.label, lang) : s;
      }
      case 'TEAM_MEMBER':
        return this.context.teamMembers?.find((m) => m.id === s)?.name ?? s;
      case 'NUMBER':
        return lang === 'sk' ? s.replace('.', ',') : s;
      case 'DATE':
        return formatSummaryDate(s, lang);
      case 'YES_NO':
        return s;
      default:
        return `${p.quoteOpen}${s}${p.quoteClose}`;
    }
  }
}

function join(items: string[], last: string): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')}${last}${items[items.length - 1]}`;
}
